import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, first } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AppUpdateService {

    private readonly checkIntervalMs = 15 * 60 * 1000;
    private initialized = false;
    private updateInProgress = false;

    constructor(
        private readonly appRef: ApplicationRef,
        private readonly swUpdate: SwUpdate,
        private readonly ngZone: NgZone,
    ) {}

    initialize(): void {
        if (this.initialized) {
            return;
        }

        this.initialized = true;

        if (!this.swUpdate.isEnabled) {
            return;
        }

        this.watchForAvailableUpdates();
        this.scheduleUpdateChecks();
    }

    private watchForAvailableUpdates(): void {
        this.swUpdate.versionUpdates
            .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
            .subscribe(() => {
                void this.activateUpdateAndReload();
            });

        this.swUpdate.unrecoverable.subscribe(() => {
            window.location.reload();
        });
    }

    private scheduleUpdateChecks(): void {
        this.appRef.isStable
            .pipe(first((isStable) => isStable))
            .subscribe(() => {
                void this.checkForUpdate();

                this.ngZone.runOutsideAngular(() => {
                    window.setInterval(() => {
                        if (!document.hidden) {
                            void this.checkForUpdate();
                        }
                    }, this.checkIntervalMs);

                    window.addEventListener('focus', () => {
                        void this.checkForUpdate();
                    });

                    document.addEventListener('visibilitychange', () => {
                        if (!document.hidden) {
                            void this.checkForUpdate();
                        }
                    });
                });
            });
    }

    private async checkForUpdate(): Promise<void> {
        try {
            await this.swUpdate.checkForUpdate();
        } catch (error) {
            console.warn('Could not check for app update.', error);
        }
    }

    private async activateUpdateAndReload(): Promise<void> {
        if (this.updateInProgress) {
            return;
        }

        this.updateInProgress = true;

        try {
            await this.swUpdate.activateUpdate();
        } catch (error) {
            console.warn('Could not activate app update.', error);
        }

        window.location.reload();
    }
}
